import React, { useState, useMemo } from 'react';
import { X, Search, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';

interface SearchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchDrawer: React.FC<SearchDrawerProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const { addToCart } = useCart();
  const { products } = useProducts();

  const filteredProducts = useMemo(() => {
    if (!query.trim()) return [];
    return products.filter(product => 
      product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.description.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, products]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 max-w-md w-full bg-simba-cream shadow-2xl flex flex-col transform transition-transform duration-300">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white">
          <h2 className="font-display font-bold text-xl text-simba-dark">
            Search
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-simba-dark">
            <X size={24} />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input 
              type="text" 
              placeholder="Search for products..." 
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-simba-green/20 focus:border-simba-green transition-all"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
          {!query && (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 text-gray-400 opacity-60">
                <Search size={48} />
                <p>Start typing to find Mufasa energy.</p>
            </div>
          )}

          {query && filteredProducts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No matches found for "{query}"</p>
            </div>
          )}

          <div className="space-y-4">
            {filteredProducts.map(product => (
              <div key={product.id} className="bg-white p-4 rounded-xl border border-gray-100 hover:border-simba-green/30 transition-colors group">
                <div className="flex gap-4">
                   <div className="w-16 h-16 bg-simba-cream rounded-lg p-2 flex-shrink-0">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain mix-blend-multiply" />
                   </div>
                   <div className="flex-1 min-w-0">
                      <Link to={`/product/${product.slug}`} onClick={onClose}>
                        <h3 className="font-bold text-simba-dark truncate group-hover:text-simba-green transition-colors">{product.name}</h3>
                      </Link>
                      <p className="text-sm text-gray-500 mb-2 truncate">{product.description}</p>
                      <div className="flex items-center justify-between">
                         <span className="font-bold text-simba-dark">£{product.price.toFixed(2)}</span>
                         <button 
                            onClick={() => addToCart(product)}
                            className="bg-simba-cream text-simba-green p-2 rounded-full hover:bg-simba-green hover:text-white transition-colors"
                            title="Add to Cart"
                         >
                            <ShoppingBag size={16} />
                         </button>
                      </div>
                   </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};
